import React from 'react';
import { Loader2, CheckCircle2, Circle, Sparkles } from 'lucide-react';
import { StructuredProgress } from '../../types';

interface ProgressDisplayProps {
  progress: StructuredProgress | null;
  fallback: string;
}

export const ProgressDisplay: React.FC<ProgressDisplayProps> = ({
  progress,
  fallback,
}) => {
  // Simple spinner fallback when no structured progress available
  if (!progress) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-5 py-4 shadow-sm max-w-lg">
        <div className="flex items-center gap-3 mb-2">
          <Loader2 className="w-5 h-5 animate-spin text-[#07E5D1]" />
          <span className="text-sm font-medium text-gray-700">{fallback}</span>
        </div>
        <p className="text-xs text-gray-400 ml-8">This may take a moment</p>
      </div>
    );
  }

  // Calculate overall progress
  const completedStages = progress.stages.filter(s => s.status === 'completed').length;
  const totalStages = progress.stages.length;
  const stageProgress = Math.round((completedStages / totalStages) * 100);

  // Calculate plan progress if available
  const completedSteps = progress.plan_steps?.filter(s => s.status === 'completed').length ?? 0;
  const runningSteps = progress.plan_steps?.filter(s => s.status === 'running').length ?? 0;
  const totalSteps = progress.plan_steps?.length ?? 0;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md shadow-sm w-full max-w-xl overflow-hidden">
      {/* Header with overall progress */}
      <div className="bg-gradient-to-r from-[#07E5D1]/10 to-[#E8FCFA] px-5 py-3 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#07E5D1]" />
            <span className="text-sm font-semibold text-gray-800">
              {progress.message || 'Processing'}
            </span>
          </div>
          <span className="text-xs text-gray-500">
            {stageProgress}% complete
          </span>
        </div>

        {/* Mini progress bar */}
        <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#07E5D1] rounded-full transition-all duration-500 ease-out"
            style={{ width: `${stageProgress}%` }}
          />
        </div>
      </div>

      {/* Stage indicators - compact horizontal view */}
      <div className="px-5 py-3 border-b border-gray-100">
        <div className="flex items-center justify-between">
          {progress.stages.map((stage, idx) => (
            <React.Fragment key={stage.stage}>
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                  stage.status === 'completed'
                    ? 'bg-[#07E5D1] text-white'
                    : stage.status === 'active'
                    ? 'bg-[#E8FCFA] border-2 border-[#07E5D1] text-[#07E5D1]'
                    : 'bg-gray-100 text-gray-400'
                }`}>
                  {stage.status === 'completed' ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : stage.status === 'active' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <span className="text-xs font-medium">{idx + 1}</span>
                  )}
                </div>
                <span className={`text-[10px] mt-1 text-center max-w-[60px] leading-tight ${
                  stage.status === 'active' ? 'text-gray-700 font-medium' : 'text-gray-400'
                }`}>
                  {stage.label.split(' ').slice(0, 2).join(' ')}
                </span>
              </div>
              {idx < progress.stages.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 rounded ${
                  stage.status === 'completed' ? 'bg-[#07E5D1]' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Plan steps as todo list */}
      {progress.plan_steps && progress.plan_steps.length > 0 && (
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Plan {progress.plan_version > 1 && <span className="text-[#07E5D1]">(updated)</span>}
            </span>
            <span className="text-xs text-gray-400">
              {completedSteps}/{totalSteps} done
            </span>
          </div>

          <ul className="space-y-2">
            {progress.plan_steps.map((step, idx) => (
              <li
                key={`${step.id}-${idx}`}
                className={`transition-all duration-300 ${
                  step.status === 'running'
                    ? 'bg-[#E8FCFA] rounded-lg p-3 -mx-2 border border-[#07E5D1]/20'
                    : 'px-1'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Status icon */}
                  <div className="flex-shrink-0 mt-0.5">
                    {step.status === 'completed' && (
                      <CheckCircle2 className="w-5 h-5 text-[#07E5D1]" />
                    )}
                    {step.status === 'running' && (
                      <div className="relative">
                        <Circle className="w-5 h-5 text-[#07E5D1]" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-2 h-2 bg-[#07E5D1] rounded-full animate-ping" />
                        </div>
                      </div>
                    )}
                    {step.status === 'pending' && (
                      <Circle className="w-5 h-5 text-gray-300" />
                    )}
                    {step.status === 'skipped' && (
                      <Circle className="w-5 h-5 text-gray-300" />
                    )}
                  </div>

                  {/* Label and progress */}
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm leading-tight block ${
                      step.status === 'completed'
                        ? 'text-gray-500 line-through'
                        : step.status === 'running'
                        ? 'text-gray-800 font-medium'
                        : 'text-gray-500'
                    }`}>
                      {step.label}
                    </span>

                    {/* Progress bar for running steps */}
                    {step.status === 'running' && (
                      <div className="mt-2">
                        <div className="h-1 bg-[#07E5D1]/20 rounded-full overflow-hidden">
                          <div className="h-full bg-[#07E5D1] rounded-full animate-progress-indeterminate" />
                        </div>
                        <p className="text-[10px] text-[#07E5D1] mt-1 flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Processing...
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {/* Activity summary */}
          {runningSteps > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Loader2 className="w-3 h-3 animate-spin text-[#07E5D1]" />
                <span>
                  {runningSteps === 1
                    ? '1 task in progress'
                    : `${runningSteps} tasks running in parallel`}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Current activity message */}
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#07E5D1] rounded-full animate-pulse" />
          <p className="text-xs text-gray-600 truncate">
            {progress.message}
          </p>
        </div>
      </div>
    </div>
  );
};
